/**
 * @fileoverview Prompt template for character-arc-extractor-v3.
 *
 * This prompt instructs the AI to analyze source text and extract character arcs
 * for each major character. It uses existing Handlebars macros for character
 * and story context.
 *
 * Shared arc concept sections are imported from the canonical schema to stay
 * in sync with character-arc-creation-v2.
 */

import {
  CHARACTER_ARC_CONCEPTS_PROMPT,
  STRUCTURE_PHASES_TABLE_PROMPT,
  ARC_PROGRESS_PROMPT,
} from '@/domains/characters/schemas';

import {
  ARC_BEAT_GUIDANCE_LONG,
  ARC_BEAT_GUIDANCE_SHORT,
  ARC_SUMMARY_GUIDANCE,
  ARC_NAMING,
  BEAT_TEXT_NORMS,
} from '@/ai/agents/shared/character-arc-guidance';

import { CHARACTER_ARC_DEFINITION } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert narrative analyst specializing in character development and story structure.

## TASK INSTRUCTIONS

Your mission is to analyze the provided source text and extract the character arc for each major character listed below. You must identify the psychological transformation journey each character undergoes throughout the story.

${CHARACTER_ARC_DEFINITION}

### Analysis Steps

1. **Review the Characters:** Examine the list of major characters provided. You will extract an arc for each one.

2. **Analyze the Source Text:** Read through the text carefully, tracking each character's:
   - Initial psychological state, beliefs, and motivations (the "Lie" they believe)
   - Key moments of change or realization (the crucible that forces growth)
   - Internal conflicts and how they evolve
   - Final psychological state and transformation (the "New Truth" they accept)

3. **Extract Character Arcs:** For each major character, construct a detailed arc with:
   - An evocative arc name that captures the *direction* of transformation
   - A summary of their transformation focusing on the internal psychological shift
   - Beats at key structural phases mapping the internal journey

**CRITICAL**: A character arc must strictly map an internal psychological shift. Do NOT produce a list of physical events — those belong to storylines and plot threads. Focus ~80% on what changes inside the character and ~20% on observable behavior that results from the shift.

### Character Arc Concepts

${CHARACTER_ARC_CONCEPTS_PROMPT}

### Structure Phases

${STRUCTURE_PHASES_TABLE_PROMPT}

### Arc Progress Values

${ARC_PROGRESS_PROMPT}

## OUTPUT REQUIREMENTS

${ARC_NAMING}

${ARC_SUMMARY_GUIDANCE}

${BEAT_TEXT_NORMS}

{{#if isShortForm}}
### SHORT-FORM EXTRACTION

This is a **short-form project**.

${ARC_BEAT_GUIDANCE_SHORT}

Produce an arc with arcName and arcSummary for each character, with an empty beats array. See the summary guidance above for appropriate length based on character category (major vs minor).

{{else}}
### LONG-FORM EXTRACTION

${ARC_BEAT_GUIDANCE_LONG}

{{/if}}

Your output uses a flat structure with two sibling arrays:

1. **characterArcs**: An array of character arc objects, one per character. Each contains:
   - **characterName**: The exact name of the character (must match the provided list)
   - **id**: A unique identifier for the arc
   - **arcName**: A short, evocative title (see naming guidance above)
   - **arcSummary**: Summary of the overall transformation (see summary guidance above for appropriate length)

2. **beats**: An array of all character arc beats across all characters. Each contains:
   - **characterIndex**: The 0-based index into the \`characterArcs\` array identifying which character this beat belongs to
   - **arcPhase**: The structural function for THIS CHARACTER's internal arc (one of the 7 phases)
   - **globalPhase**: Where this beat sits in the OVERALL story timeline (one of the 7 phases)
   - **internalState**: The psychological shift (required)
   - **behavior**: Observable action (optional but recommended)
   - **arcProgress**: Number from -1 to +1

**CRITICAL — globalPhase vs arcPhase:**
- For characters whose arc spans the entire story: globalPhase equals arcPhase.
- For characters introduced late, killed early, or with compressed arcs: arcPhase DIFFERS from globalPhase. A character introduced at the global Midpoint would have their first beat with globalPhase="Midpoint" but arcPhase="Initial State".
- You MUST provide BOTH fields for every beat. They are separate fields, not aliases.

If a character does not have a discernible arc in the text (appears only briefly, no development), you may omit them from the output.

---

## PRIMARY TASK CONTEXT

**MAJOR CHARACTERS TO ANALYZE**
<<<characterRoles>>>

---

## SECONDARY TASK CONTEXT

<<<storyContext>>>

---

## SOURCE TEXT TO ANALYZE

{{#if textInput}}
{{{textInput}}}
{{else}}
No source text provided.
{{/if}}

---

## OUTPUT INSTRUCTIONS

Provide your output as a JSON object with a \`characterArcs\` array (one entry per character with \`characterName\`, \`id\`, \`arcName\`, \`arcSummary\`) and a \`beats\` array (all beats with \`characterIndex\` references, each carrying both \`globalPhase\` and \`arcPhase\`).
`;
